// NOTE: This is copy-pasta from /components/VennDiagram. I'm not too worried about it, but just be aware that this code is largely duplicated.
import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';
import useMousePosition from '@react-hook/mouse-position';

import { normalize } from '@/utils';

import { useColorMode } from '@/components/UserPreferencesProvider';

const springConfig = {
  tension: 110,
  friction: 19,
};
const OFFSET_BY = 25;

const RADIUS = 150;
const DIAMETER = RADIUS * 2;
const OVERLAP_RATIO = 0.5;

const TOTAL_WIDTH = DIAMETER * 2 - DIAMETER * OVERLAP_RATIO;
const VIEWBOX = `0 0 ${TOTAL_WIDTH} ${DIAMETER}`;

const LEFT_CENTER = { x: RADIUS - OFFSET_BY, y: RADIUS };
const RIGHT_CENTER = {
  x: TOTAL_WIDTH - RADIUS + OFFSET_BY,
  y: RADIUS,
};

const FlexVennDiagram = () => {
  const colorMode = useColorMode();

  const SCALE_BY = 1.1;

  const [hovering, setHovering] = React.useState({
    left: false,
    right: false,
  });

  const ref = React.useRef();

  const mousePosition = useMousePosition(ref);

  React.useEffect(() => {
    if (!mousePosition.x) {
      setHovering({ left: false, right: false });
      return;
    }

    const xInViewbox = normalize(
      mousePosition.x,
      0,
      mousePosition.elementWidth,
      0,
      TOTAL_WIDTH
    );
    const yInViewbox = normalize(
      mousePosition.y,
      0,
      mousePosition.elementHeight,
      0,
      DIAMETER
    );

    const distanceFromLeft = Math.sqrt(
      Math.abs(xInViewbox - LEFT_CENTER.x) ** 2 +
        Math.abs(yInViewbox - LEFT_CENTER.y) ** 2
    );

    const distanceFromRight = Math.sqrt(
      Math.abs(xInViewbox - RIGHT_CENTER.x) ** 2 +
        Math.abs(yInViewbox - RIGHT_CENTER.y) ** 2
    );

    setHovering({
      left: distanceFromLeft < RADIUS,
      right: distanceFromRight < RADIUS,
    });
  }, [mousePosition]);

  const hoveringOverCenter = hovering.left && hovering.right;
  const notHoveringAnything = !hovering.left && !hovering.right;

  const leftCircleStyle = useSpring({
    transform: hovering.left ? `scale(${SCALE_BY})` : 'scale(1)',
    fillOpacity: hovering.left ? 0.5 : 0.35,
    cx: hoveringOverCenter
      ? LEFT_CENTER.x + 10
      : hovering.left
        ? LEFT_CENTER.x - 20
        : LEFT_CENTER.x,
    config: springConfig,
  });

  const rightCircleStyle = useSpring({
    transform: hovering.right ? `scale(${SCALE_BY})` : 'scale(1)',
    fillOpacity: hovering.right ? 0.5 : 0.35,
    cx: hoveringOverCenter
      ? RIGHT_CENTER.x - 10
      : hovering.right
        ? RIGHT_CENTER.x + 20
        : RIGHT_CENTER.x,
    config: springConfig,
  });

  const shiftTextBy = mousePosition.elementWidth * 0.05;

  const leftStyles = {
    transform: hovering.left
      ? `translateX(-${shiftTextBy}px) scale(1.1)`
      : `translateX(0) scale(1)`,
    // prettier-ignore
    opacity: notHoveringAnything
      ? 0.75
      : hovering.left
        ? 1
        : 0.25,
  };
  const centerStyles = {
    transform: hoveringOverCenter
      ? `scale(1.1) translateX(0px)`
      : hovering.left
        ? `scale(1.1) translateX(-30px)`
        : hovering.right
          ? `scale(1.1) translateX(30px)`
          : `scale(1) translateX(0px)`,
    opacity: notHoveringAnything
      ? 0.75
      : hovering.left || hovering.right
        ? 1
        : 0.25,
  };
  const rightStyles = {
    transform: hovering.right
      ? `translateX(${shiftTextBy}px) scale(1.1)`
      : `translateX(0) scale(1)`,
    // prettier-ignore
    opacity: notHoveringAnything
      ? 0.75
      : hovering.right
        ? 1
        : 0.25,
  };

  const circleStrokeColor =
    colorMode === 'light'
      ? 'var(--color-primary)'
      : 'hsl(50deg 100% 50%)';

  return (
    <Wrapper>
      <Svg viewBox={VIEWBOX} ref={ref}>
        <Circle
          cx={RIGHT_CENTER.x}
          cy={RIGHT_CENTER.y}
          r={RADIUS}
          stroke={
            hovering.right && !hovering.left
              ? circleStrokeColor
              : null
          }
          strokeWidth={3}
          fill="var(--color-venn-1)"
          {...rightCircleStyle}
        />
        <Circle
          cx={LEFT_CENTER.x}
          cy={LEFT_CENTER.y}
          r={RADIUS}
          stroke={
            hovering.left && !hovering.right
              ? circleStrokeColor
              : null
          }
          strokeWidth={3}
          fill="var(--color-venn-0)"
          {...leftCircleStyle}
        />
      </Svg>
      <TitleLayer>
        <LeftTitle style={leftStyles}>justify-content</LeftTitle>
        <RightTitle style={rightStyles}>align-items</RightTitle>
      </TitleLayer>
      <TextLayer>
        <Left style={leftStyles}>
          <TextButton
            onFocus={() => setHovering({ left: true, right: false })}
            onBlur={() => setHovering({ left: false, right: false })}
          >
            <Item>space-between</Item>
            <Item>space-around</Item>
            <Item>space-evenly</Item>
          </TextButton>
        </Left>
        <Right style={rightStyles}>
          <TextButton
            onFocus={() => setHovering({ left: false, right: true })}
            onBlur={() => setHovering({ left: false, right: false })}
          >
            <Item>stretch</Item>
            <Item>baseline</Item>
          </TextButton>
        </Right>
        <Center style={centerStyles}>
          <Item>flex-start</Item>
          <Item>center</Item>
          <Item>flex-end</Item>
        </Center>
      </TextLayer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  --color-venn-0: var(--color-gray-300);
  --color-venn-1: var(--color-gray-300);

  position: relative;
  margin: 48px auto;
  width: 100%;
  max-width: 500px;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Svg = styled.svg`
  display: block;
  overflow: visible;
`;

const Circle = styled(animated.circle)`
  transform-origin: 50% 50%;
`;

const TitleLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Title = styled(animated.div)`
  position: absolute;
  top: 32px;
  width: 66.666%;
  text-align: center;
  font-family: var(--font-family-mono);
  font-size: 0.875rem;
  font-weight: var(--font-weight-medium);
  transition:
    transform 500ms,
    opacity 400ms;
  will-change: transform;
`;

const LeftTitle = styled(Title)`
  left: -${OFFSET_BY}px;
`;
const RightTitle = styled(Title)`
  right: -${OFFSET_BY}px;
`;

const TextLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`;

const Text = styled(animated.div)`
  flex: 1;
  transition:
    transform 500ms,
    opacity 400ms;
  will-change: transform;
`;

const Left = styled(Text)`
  padding-left: 20px;
  order: 1;
`;
const Right = styled(Text)`
  padding-right: 20px;
  order: 3;
`;
const Center = styled(Text)`
  order: 2;
  text-align: center;
`;

const TextButton = styled.button`
  width: 100%;
  padding: 0 20px;
  text-align: center;
  color: var(--color-text);

  ${Left} & {
    text-align: left;
  }
`;

const Item = styled.span`
  display: block;
  font-size: 1rem;
`;

export default FlexVennDiagram;
